const { getStayType } = require('./stayTypeService');

const MAX_LIST_ITEMS = 20;

const PARSED_PRICE_PRECISION = parseInt(process.env.REACT_APP_PRICE_PRECISION, 10);
const DISPLAY_PRICE_PRECISION = Number.isNaN(PARSED_PRICE_PRECISION) ? 0 : PARSED_PRICE_PRECISION;
const DECIMAL_BASE = 10;

const stableStringify = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map((item) => stableStringify(item === undefined ? null : item)).join(',')}]`;
    }

    if (value && typeof value === 'object') {
        const entries = Object.keys(value)
            .filter((key) => value[key] !== undefined)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`);
        return `{${entries.join(',')}}`;
    }

    return JSON.stringify(value === undefined ? null : value);
};

const getTimestampMillis = (value) => {
    const millis = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(millis) ? millis : undefined;
};

// Hotel ids and counts are left out on purpose: Firestore polls grow the list and must not count as a new version.
const buildListVersion = ({ searchToken, searchSessionId, sort, filters } = {}) => {
    if (!searchToken) {
        return null;
    }

    return stableStringify([searchToken, searchSessionId ?? null, sort ?? null, filters ?? null]);
};

const createListVersionTracker = () => {
    let lastFired = null;

    return {
        shouldFire(signature) {
            if (!signature || signature === lastFired) {
                return false;
            }

            lastFired = signature;
            return true;
        },
        reset() {
            lastFired = null;
        },
    };
};

// Only lists the user actually sees count: real cards, not loaders, once results stopped streaming.
const shouldReportRenderedList = ({ tracker, version, renderedCount, isLoading, isCompleted } = {}) => {
    if (!tracker || isLoading || !isCompleted || !(renderedCount > 0)) {
        return false;
    }

    return tracker.shouldFire(version);
};

// Records which hotel each committed card shows, so a list is reported only once the screen shows it.
const createRenderedCardsTracker = () => {
    const idsByIndex = new Map();

    return {
        commit(index, id) {
            idsByIndex.set(index, id);
        },
        remove(index, id) {
            if (idsByIndex.get(index) === id) {
                idsByIndex.delete(index);
            }
        },
        countMatching(getExpectedId) {
            for (const [index, id] of idsByIndex) {
                if (getExpectedId(index) !== id) {
                    return 0;
                }
            }

            return idsByIndex.size;
        },
    };
};

// Only the first list of a search session waited for results; remounts and re-sorts did not.
const createSessionWaitTimeTracker = () => {
    let lastSessionKey = null;

    return {
        measure({ searchToken, startedAt, now = Date.now() } = {}) {
            const startedMillis = getTimestampMillis(startedAt);
            const sessionKey = searchToken ? `${searchToken}:${startedMillis}` : null;
            if (!sessionKey || sessionKey === lastSessionKey) {
                return;
            }

            lastSessionKey = sessionKey;
            return startedMillis === undefined ? undefined : Math.max(0, Math.ceil(now - startedMillis));
        },
    };
};

// Mirrors the hotel cards: the breakfast filter swaps the displayed deal for the breakfast one.
const getDisplayedDeal = (hotel, breakfastOnly) =>
    breakfastOnly
        ? hotel.representativeBreakfastDeal ?? hotel.representative_deals?.breakfast
        : hotel.representativeDeal ?? hotel.representative_deals?.cheapest;

// Same math and precision as Currencies.getPriceWithDisplayCurrencyByDefaultPrecision, which only returns text.
const toDisplayPrice = (price, exchangeRate) => {
    const factor = Math.pow(DECIMAL_BASE, DISPLAY_PRICE_PRECISION);
    return Math.round(Math.max(price * exchangeRate, 0) * factor) / factor;
};

// ELAL prices an item by the reservation total the card shows, in the user's display currency.
const getDisplayedTotal = (deal, { displayCurrency, displayExchangeRate } = {}) => {
    const totalPrice = Number(deal?.totalPrice);
    const exchangeRate = Number(displayExchangeRate);
    if (
        deal?.totalPrice === null ||
        deal?.totalPrice === undefined ||
        !Number.isFinite(totalPrice) ||
        !displayCurrency ||
        !Number.isFinite(exchangeRate) ||
        exchangeRate <= 0
    ) {
        return null;
    }

    return { total_price: toDisplayPrice(totalPrice, exchangeRate), total_price_currency: displayCurrency };
};

const buildHotelsResultsItems = (hotels, search, { breakfastOnly = false, ...display } = {}) => {
    if (!Array.isArray(hotels)) {
        return [];
    }

    return hotels
        .slice(0, MAX_LIST_ITEMS)
        .filter(Boolean)
        .map((hotel, index) => {
            const deal = getDisplayedDeal(hotel, breakfastOnly);
            const currency = deal?.currency || hotel.currency;
            const stayType = getStayType(hotel.category_flags);

            return {
                hotel_id: hotel.id || hotel._id || hotel.hotel_id,
                item_id: hotel.id || hotel._id || hotel.hotel_id,
                hotel_name: hotel.name || hotel.title,
                item_name: hotel.name || hotel.title,
                stars: hotel.stars,
                item_varient: hotel.autocomplete_category || hotel.stay_type,
                ...(stayType && { hotel_stay_type: stayType }),
                price_per_night: deal?.pricePerNight ?? hotel.pricePerNight,
                ...(currency && { currency }),
                ...getDisplayedTotal(deal, display),
                index: index + 1,
                city: hotel.city,
                country: hotel.country,
                ...(hotel.country_code && { country_code: hotel.country_code }),
                address: hotel.address,
                rooms: search?.rooms,
                nights: deal?.nights || search?.nights,
            };
        });
};

const buildHotelsResultsDestination = (destination) => {
    if (!destination) {
        return;
    }

    return destination.place ? destination : { place: destination };
};

module.exports = {
    MAX_LIST_ITEMS,
    getTimestampMillis,
    buildListVersion,
    createListVersionTracker,
    shouldReportRenderedList,
    createRenderedCardsTracker,
    createSessionWaitTimeTracker,
    buildHotelsResultsItems,
    buildHotelsResultsDestination,
    getDisplayedTotal,
};
